import {
  createFileRoute,
  useCanGoBack,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Capacitor } from '@capacitor/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { associateHousehold, capture } from '@tupai/analytics/client';
import {
  getOffering,
  currentActiveProductId,
  currentActiveSubscriptionId,
  getCustomerInfo,
  activeEntitlementStore,
  isNative,
  isAndroid,
  PurchasePendingError,
  PurchaseOwnershipError,
  PurchaseCancelledError,
  DeferredChangeScheduledError,
  type PurchasesOffering,
  type PurchasesPackage,
  type AppStore,
} from '@tupai/billing';
import { getBillingManagementUrl, purchaseStoreForPlatform } from '@/lib/billing-management';
import { usePendingPurchase } from '@/lib/use-pending-purchase';
import { getPendingPurchase, rememberPendingPurchase } from '@/lib/pending-purchase-store';
import { useAuthStore } from '@/store/auth-store';
import {
  BillingIdentityError,
  useBillingAvailable,
  purchasePackage,
  restorePurchases,
} from '@/lib/billing';
import {
  ApiError,
  getAccess,
  getHousehold,
  getPairingCodeInfo,
  changePlan,
  cancelPendingChange,
  recordPendingChange,
  getWebOfferings,
  createCheckoutSession,
  getCheckoutSessionStatus,
  createCardSetup,
  completeCardSetup,
  SEAT_TIER_NAMES,
  type AccessResponse,
  type HouseholdResponse,
} from '@/lib/api';
import {
  SEAT_TIERS,
  PRICING_TIERS,
  TRIAL_DAYS,
  TRIAL_REMINDER_DAY,
  seatTier,
  dateInDays,
  isSubscribed,
  planChangeBilling,
  defaultPricingTier,
  type SeatTier,
} from '@/lib/plans';
import { afterPlanPath } from '@/lib/invite-flow';
import { getPendingInvite, hasPendingInvite } from '@/lib/pending-invite';
import SeatTierCard from '@/components/seat-tier-card';
import SeatPickerSheet from '@/components/seat-picker-sheet';
import DowngradeSyllabusBlockedSheet from '@/components/downgrade-syllabus-blocked-sheet';
import EmbeddedCheckout from '@/components/embedded-checkout';
import { useLinksStore } from '@/store/links-store';
import { useTranslation } from '@tupai/i18n/react';
import { useLanguage } from '@/lib/language';
import { fmtDayMonth } from '@/lib/format';
import {
  IconX,
  IconChevronLeft,
  IconSparkles,
  IconLockOpen,
  IconBell,
  IconCreditCard,
  IconDeviceMobile,
  IconRefresh,
  IconExternalLink,
} from '@tabler/icons-react';
import excited from '@tupai/tokens/assets/spot/excited.svg';

export const Route = createFileRoute('/_protected/paywall')({
  validateSearch: z.object({
    trigger: z.string().optional(),
    checkout_session: z.string().optional(),
    card_setup: z.string().optional(),
  }),
  component: PaywallPage,
});

function PaywallPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const location = useLocation();
  const search = Route.useSearch();
  const locale = useLanguage();
  const { t } = useTranslation('account');
  // Fresh signups (trigger 'onboarding', no `from`) must pick a plan, so no X; Profile's "Upgrade now" sends the same trigger with `from` and keeps its X.
  const paywallState = location.state;
  const isOnboardingEntry = paywallState?.trigger === 'onboarding' && !paywallState?.from;
  // A caller can preselect a seat (e.g. the invite blocked screen's "Upgrade
  // to Family Duo"). Only a known SEAT_TIERS id counts; anything else is ignored.
  const requestedSeat = SEAT_TIERS.find((t) => t.id === paywallState?.seat)?.id ?? null;
  const [step, setStep] = useState<1 | 2>(1);
  const [seat, setSeat] = useState(requestedSeat ?? 'individual');
  const [pricing, setPricing] = useState('standard');
  const [annual, setAnnual] = useState(false);
  const [offerings, setOfferings] = useState<Record<string, PurchasesOffering | null>>({});
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Deferred change: unix seconds, or null when Stripe gave no period end (still the scheduled screen); undefined = immediate.
  const [deferredUntil, setDeferredUntil] = useState<number | null | undefined>(undefined);
  const [error, setError] = useState('');
  const [checkout, setCheckout] = useState<{
    kind: 'purchase' | 'card';
    clientSecret: string;
    sessionId: string;
  } | null>(null);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

  const native = useBillingAvailable();
  // Real Capacitor native; not the same as `native` (whether purchases work at all here).
  const nativePlatform = isNative();
  const selectedTier = seatTier(seat);

  const [access, setAccess] = useState<AccessResponse | null>(null);
  const finishPendingPurchase = useCallback((confirmed: AccessResponse) => {
    if (confirmed.seat_tier) setSeat(confirmed.seat_tier);
    setPricing(confirmed.pricing_tier);
    setDone(true);
  }, []);
  const pendingPurchase = usePendingPurchase(native, setAccess, finishPendingPurchase);
  useEffect(() => {
    getAccess()
      .then((a) => {
        setAccess(a);
        if (a.seat_tier) {
          // A preselected seat stays put until the parent is subscribed.
          if (isSubscribed(a) || !requestedSeat) setSeat(a.seat_tier);
          setPricing(a.pricing_tier);
        }
      })
      .catch(() => {});
  }, [requestedSeat]);
  const ownedSeat = access?.seat_tier ?? null;
  // Status decides, not seat_tier: payment_pending and lapsed households still carry a placeholder/old seat_tier.
  const subscribed = access !== null && isSubscribed(access);
  const currentSeat = subscribed ? ownedSeat : null;
  // A downgrade is scheduled and the current plan is selected: the parent is calling it off.
  const revertingPending =
    Boolean(access?.pending_seat_tier) && seat === currentSeat && pricing === access?.pricing_tier;
  // pending_pricing_tier is only compared when set; Google Play records a pending change without one.
  const alreadyPending =
    Boolean(access?.pending_seat_tier) &&
    seat === access?.pending_seat_tier &&
    (!access?.pending_pricing_tier || pricing === access.pending_pricing_tier);
  const changing =
    subscribed &&
    !alreadyPending &&
    (seat !== currentSeat || pricing !== access?.pricing_tier || revertingPending);
  // Gated on seat tier, not `changing`: a pricing switch on a Solo trial keeps the remaining trial days.
  const endsTrialNow =
    subscribed && access?.status === 'trial' && seat !== currentSeat && !selectedTier.trial;

  const [ownedStore, setOwnedStore] = useState<AppStore | null | undefined>(undefined);
  const [ownershipRevision, setOwnershipRevision] = useState(0);
  useEffect(() => {
    let disposed = false;
    if (!subscribed) {
      setOwnedStore(null);
      return;
    }
    // The backend describes this household's subscription even when its owner bought elsewhere.
    if (access?.store && access.store !== 'UNKNOWN_STORE') {
      setOwnedStore(access.store);
      return;
    }
    if (!nativePlatform || !native) {
      setOwnedStore(null);
      return;
    }
    setOwnedStore(undefined);
    getCustomerInfo()
      .then((ci) => {
        const store = activeEntitlementStore(ci);
        if (!disposed) setOwnedStore(store === 'UNKNOWN_STORE' ? null : store);
      })
      .catch(() => {
        if (!disposed) setOwnedStore(null);
      });
    return () => {
      disposed = true;
    };
  }, [subscribed, nativePlatform, native, access?.store, ownershipRevision]);
  const purchaseStore = purchaseStoreForPlatform(Capacitor.getPlatform());
  const storeMismatch = subscribed && ownedStore != null && ownedStore !== purchaseStore;
  const ownershipBlocked = access === null || (subscribed && (ownedStore == null || storeMismatch));
  const [managementBusy, setManagementBusy] = useState(false);
  const [managementError, setManagementError] = useState('');

  const [household, setHousehold] = useState<HouseholdResponse['household']>(null);
  const refreshHousehold = () =>
    getHousehold().then((r) => {
      setHousehold(r.household);
      if (r.household?.id) associateHousehold(r.household.id);
    });
  useEffect(() => {
    refreshHousehold().catch(() => {});
  }, []);
  const seatedStudents = household?.members.filter((m) => m.role === 'student').length ?? 0;

  const blockedReason = (maxStudents: number): string | null =>
    seatedStudents > maxStudents ? t('blockedTooManyChildren', { count: seatedStudents }) : null;

  const parentLinks = useLinksStore((s) => s.parentLinks);
  const [pickerTier, setPickerTier] = useState<SeatTier | null>(null);
  const onReleased = async () => {
    setPickerTier(null);
    await refreshHousehold().catch(() => {});
    if (pickerTier) setSeat(pickerTier.id);
    setStep(2);
  };

  const igcseChildren =
    household?.members.filter((m) => m.role === 'student' && m.syllabus_id === 'IGCSE') ?? [];
  const attemptingSyllabusDowngrade =
    subscribed && access?.pricing_tier === 'premium' && pricing === 'standard';
  const syllabusDowngradeBlocked = attemptingSyllabusDowngrade && igcseChildren.length > 0;
  const [showSyllabusBlock, setShowSyllabusBlock] = useState(false);

  // Open on + IGCSE when a linked child, or the child whose
  // invitation the parent is accepting, studies IGCSE. Only before the parent
  // has a plan (a subscriber starts on their own plan), and never after the
  // parent picked a syllabus themselves.
  const [inviteSyllabus, setInviteSyllabus] = useState<string | null>(null);
  useEffect(() => {
    const invite = getPendingInvite();
    if (!invite) return;
    getPairingCodeInfo(invite.code)
      .then((info) => setInviteSyllabus(info.child_syllabus ?? null))
      .catch(() => {});
  }, []);
  const pricingPicked = useRef(false);
  const suggestedPricing = defaultPricingTier([
    ...parentLinks.filter((l) => l.status === 'approved').map((l) => l.student_syllabus),
    ...igcseChildren.map((m) => m.syllabus_id),
    inviteSyllabus,
  ]);
  useEffect(() => {
    if (pricingPicked.current || (access && isSubscribed(access))) return;
    if (suggestedPricing === 'premium') setPricing('premium');
  }, [suggestedPricing, access]);

  useEffect(() => {
    if (!native) return;
    setOfferings({});
    setLoadError('');
    const web = nativePlatform ? null : getWebOfferings();
    Promise.all(
      SEAT_TIERS.map((t) =>
        (web
          ? web.then((all) => all[`${t.id}_${pricing}`] ?? null)
          : getOffering(`${t.id}_${pricing}`)
        )
          .then((o) => [t.id, o] as const)
          .catch(() => [t.id, null] as const)
      )
    )
      .then((pairs) => {
        const next = Object.fromEntries(pairs);
        setOfferings(next);
        if (Object.values(next).every((o) => o === null)) {
          setLoadError(t('errLoadPrices'));
        }
      })
      .catch(() => setLoadError(t('errLoadPrices')));
  }, [native, nativePlatform, pricing]);

  // Deliberately reports the first-render seat/pricing, before getAccess overrides them.
  const paywallViewedRef = useRef(false);
  // Onboarding arrives via replace navigations, so only pop when there is an entry behind this one.
  const closePaywall = () => {
    if (hasPendingInvite()) {
      navigate({ to: afterPlanPath(), replace: true });
      return;
    }
    if (canGoBack) {
      router.history.back();
      return;
    }
    navigate({ to: '/children', replace: true });
  };

  useEffect(() => {
    if (paywallViewedRef.current) return;
    paywallViewedRef.current = true;
    const navState = location.state;
    capture('paywall_viewed', {
      offering: `${seat}_${pricing}`,
      // Notification links carry it as ?trigger=, since a path can't hold router state.
      trigger: navState?.trigger ?? search.trigger ?? 'direct',
    });
  }, []);

  const offering = offerings[seat] ?? null;
  const monthly = offering?.availablePackages.find((p) => p.packageType === 'MONTHLY') ?? null;
  const yearly = offering?.availablePackages.find((p) => p.packageType === 'ANNUAL') ?? null;
  const selected: PurchasesPackage | null = annual ? (yearly ?? monthly) : monthly;
  // 0 = no saving to show (prices missing or annual not cheaper).
  const annualSave =
    monthly?.product.price && yearly?.product.price
      ? Math.ceil(100 - (yearly.product.price / (12 * monthly.product.price)) * 100)
      : 0;

  const monthlyPriceFor = (tierId: string): string | null =>
    offerings[tierId]?.availablePackages.find((p) => p.packageType === 'MONTHLY')?.product
      .priceString ?? null;

  // Null when the period can't be determined; planChangeBilling then uses 'store-default' (not 'defer', which would delay upgrades).
  const currentAnnual = (activeSubscriptionId: string | null): boolean | null => {
    if (!activeSubscriptionId || !ownedSeat || pricing !== access?.pricing_tier) return null;
    const owned = offerings[ownedSeat];
    if (!owned) return null;
    // Match the full store id: monthly and annual share one bare id.
    const match = owned.availablePackages.find(
      (p) => p.product.identifier === activeSubscriptionId
    );
    if (!match) return null;
    return match.packageType === 'ANNUAL';
  };

  async function confirmBackendSync(
    matches: (access: AccessResponse) => boolean = (a) =>
      a.status === 'trial' || a.status === 'active'
  ): Promise<AccessResponse | null> {
    // Ownership rejections bypass this fallback; existing access cannot prove that purchase succeeded.
    for (let i = 0; i < 5; i++) {
      const access = await getAccess();
      if (matches(access)) return access;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  }

  async function confirmPlanChangeSync(
    initialSeat: string,
    initialPricing: string
  ): Promise<boolean> {
    for (let i = 0; i < 5; i++) {
      const access = await getAccess();
      if (access.seat_tier !== initialSeat || access.pricing_tier !== initialPricing) return true;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return false;
  }

  // A complete Session only proves Stripe took the order; access comes from the webhook, so poll.
  async function finishWebCheckout(sessionId: string) {
    setCheckout(null);
    setBusy(true);
    setError('');
    try {
      const session = await getCheckoutSessionStatus(sessionId);
      if (session.status !== 'complete') {
        setPendingSessionId(null);
        if (session.status === 'expired') setError(t('errPurchaseFailed'));
        return;
      }
      if (!(await confirmBackendSync())) {
        setPendingSessionId(sessionId);
        return;
      }
      const a = await getAccess();
      setAccess(a);
      if (a.seat_tier) {
        setSeat(a.seat_tier);
        setPricing(a.pricing_tier);
      }
      setPendingSessionId(null);
      setDone(true);
    } catch {
      setPendingSessionId(sessionId);
    } finally {
      setBusy(false);
    }
  }

  // Stripe redirects back here only for redirect-based payment methods.
  useEffect(() => {
    const sessionId = search.checkout_session;
    if (!sessionId) return;
    navigate({ to: '.', search: {}, replace: true, state: true });
    setStep(2);
    void finishWebCheckout(sessionId);
  }, []);

  async function startWebCheckout() {
    try {
      const session = await createCheckoutSession({
        seatTier: seat,
        pricingTier: pricing,
        billingPeriod: annual ? 'yearly' : 'monthly',
      });
      setCheckout({
        kind: 'purchase',
        clientSecret: session.client_secret,
        sessionId: session.session_id,
      });
    } catch {
      setError(t('errPurchaseFailed'));
    }
  }

  async function startCardSetup() {
    try {
      const session = await createCardSetup();
      setCheckout({
        kind: 'card',
        clientSecret: session.client_secret,
        sessionId: session.session_id,
      });
    } catch {
      setError(t('errAddCard'));
    }
  }

  async function finishCardSetup(sessionId: string) {
    setCheckout(null);
    setBusy(true);
    setError('');
    try {
      await completeCardSetup(sessionId);
    } catch {
      setError(t('errAddCard'));
      setBusy(false);
      return;
    }
    await buy(true);
  }

  // Redirect-based card setup only; the plan selection is lost on reload, so the parent picks again.
  useEffect(() => {
    const sessionId = search.card_setup;
    if (!sessionId) return;
    navigate({ to: '.', search: {}, replace: true, state: true });
    completeCardSetup(sessionId).catch(() => setError(t('errAddCard')));
  }, []);

  async function buy(afterCardSetup = false) {
    if (
      !access ||
      pendingPurchase.pending ||
      (subscribed && (!ownedStore || ownedStore !== purchaseStore))
    )
      return;
    const purchaseParentId = useAuthStore.getState().parentId;
    setBusy(true);
    setError('');
    try {
      // Calling off a scheduled downgrade only releases the Stripe schedule; keep it off changePlan's path.
      if (revertingPending) {
        await cancelPendingChange();
        await getAccess()
          .then(setAccess)
          .catch(() => {});
        setDone(true);
        return;
      }

      if (changing && !nativePlatform) {
        capture('checkout_started', {
          offering: `${seat}_${pricing}`,
          product_id: selected?.product.identifier,
        });
        const res = await changePlan({
          seatTier: seat,
          pricingTier: pricing,
          billingPeriod: annual ? 'yearly' : 'monthly',
        });
        // A deferred downgrade changes nothing today, so polling for the tier would just time out.
        if (res.deferred_until !== undefined) {
          setDeferredUntil(res.deferred_until);
          setDone(true);
          return;
        }
        // Money has already moved here, so not errPurchaseFailed (which says nothing was charged).
        const synced = await confirmPlanChangeSync(ownedSeat!, access!.pricing_tier);
        if (!synced) {
          setError(t('errPlanChangeNotReflected'));
          return;
        }
        setDone(true);
        return;
      }
      if (!selected) return;
      capture('checkout_started', {
        offering: `${seat}_${pricing}`,
        product_id: selected.product.identifier,
      });
      if (!nativePlatform) {
        await startWebCheckout();
        return;
      }
      if (isAndroid()) {
        // Plan change on an active sub → Play replacement, never a 2nd sub.
        const oldId = await currentActiveProductId();
        // Bare id for Play's replacement API; full id (base plan intact) to tell the billing period.
        const oldSubscriptionId = await currentActiveSubscriptionId();
        const isChange = oldId !== null && oldId !== selected.product.identifier.split(':')[0];
        // Ending a trial always charges now, whichever direction the tiers moved.
        const changeBilling = endsTrialNow
          ? 'charge-now'
          : isChange
            ? planChangeBilling(
                {
                  seatTier: ownedSeat ?? seat,
                  pricingTier: access?.pricing_tier ?? pricing,
                  annual: currentAnnual(oldSubscriptionId),
                },
                { seatTier: seat, pricingTier: pricing, annual }
              )
            : 'store-default';
        await purchasePackage(selected, isChange ? oldId : undefined, changeBilling);
      } else {
        await purchasePackage(selected);
      }
      // Poll on tier, not status (already active); gate on `changing`, not `isChange`, since a period-only switch never moves the tier.
      const synced = await (changing
        ? confirmPlanChangeSync(ownedSeat!, access!.pricing_tier)
        : confirmBackendSync());
      if (!synced) {
        setError(t('purchaseStillSyncing'));
        return;
      }
      setDone(true);
    } catch (e) {
      if (e instanceof PurchaseCancelledError) return;
      if (e instanceof PurchaseOwnershipError) {
        setError(t('purchaseLinkedToAnotherAccount'));
        return;
      }
      if (e instanceof PurchasePendingError) {
        if (purchaseParentId && selected && !getPendingPurchase(purchaseParentId))
          rememberPendingPurchase(purchaseParentId, {
            productId: selected.product.identifier,
            seatTier: seat,
            pricingTier: pricing,
          });
        return;
      }
      if (e instanceof BillingIdentityError) {
        setError(t('errPurchaseFailed'));
        return;
      }
      // No card on file: collect one, then retry once.
      if (e instanceof ApiError && e.code === 'payment_method_required') {
        if (afterCardSetup) setError(t('errAddCard'));
        else await startCardSetup();
        return;
      }
      if (e instanceof ApiError && e.code === 'payment_failed') {
        setError(t('errAddCard'));
        return;
      }
      // Play accepted a deferred downgrade: not a failure. Play gives no effective date, hence null.
      if (e instanceof DeferredChangeScheduledError) {
        // Swallowed on purpose: the change already happened at Play. Stripe records its own in change-plan.
        try {
          await recordPendingChange({ seatTier: seat, pricingTier: pricing });
          await getAccess().then(setAccess);
        } catch (noteErr) {
          console.warn('[billing] deferred change scheduled but note not recorded', noteErr);
        }
        setDeferredUntil(null);
        setDone(true);
        return;
      }
      // An SDK rejection isn't proof of failure; the webhook-written household decides.
      const confirmed = changing
        ? await confirmPlanChangeSync(ownedSeat!, access!.pricing_tier)
        : await confirmBackendSync();
      if (confirmed) {
        setDone(true);
      } else {
        setError(t('errPurchaseFailed'));
      }
    } finally {
      setBusy(false);
    }
  }

  const openStoreBilling = async () => {
    if (managementBusy) return;
    setManagementBusy(true);
    setManagementError('');
    try {
      const url = await getBillingManagementUrl(ownedStore ?? null);
      if (!url) {
        setManagementError(t('billingManagementUnavailable'));
        return;
      }
      if (nativePlatform) window.open(url, '_blank');
      else window.location.assign(url);
    } catch {
      setManagementError(t('errManageBilling'));
    } finally {
      setManagementBusy(false);
    }
  };

  const retryBillingOwnership = async () => {
    setManagementError('');
    setOwnedStore(undefined);
    try {
      setAccess(await getAccess());
    } catch {
      setManagementError(t('errManageBilling'));
    } finally {
      setOwnershipRevision((revision) => revision + 1);
    }
  };

  async function restore() {
    setBusy(true);
    setError('');
    try {
      const { customerInfo } = await restorePurchases();
      const restoredSeats = new Set(
        Object.values(customerInfo.entitlements.active)
          .filter(
            (entitlement) => entitlement.isActive && entitlement.identifier.startsWith('seat_')
          )
          .map((entitlement) => entitlement.identifier)
      );
      if (restoredSeats.size === 0) {
        setError(t('errNothingToRestore'));
        return;
      }
      const restored = await confirmBackendSync(
        (a) => isSubscribed(a) && restoredSeats.has(`seat_${a.seat_tier}`)
      );
      if (!restored) {
        setError(t('restoreStillSyncing'));
        return;
      }
      setAccess(restored);
      if (restored.seat_tier) setSeat(restored.seat_tier);
      setPricing(restored.pricing_tier);
      setDone(true);
    } catch (e) {
      if (e instanceof PurchaseCancelledError) return;
      setError(
        t(
          e instanceof PurchaseOwnershipError
            ? 'purchaseLinkedToAnotherAccount'
            : 'errRestoreFailed'
        )
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-surface p-8 text-center">
        <img src={excited} alt="" className="size-16" />
        <h1 className="font-display text-2xl text-content">
          {deferredUntil !== undefined ? t('planChangeScheduled') : t('allSet')}
        </h1>
        <p className="text-base text-muted">
          {deferredUntil === undefined
            ? t('planActive', { tierName: selectedTier.name })
            : deferredUntil === null
              ? t('planChangeScheduledSoon', { tierName: selectedTier.name })
              : t('planChangeScheduledOn', {
                  tierName: selectedTier.name,
                  date: fmtDayMonth(new Date(deferredUntil * 1000).toISOString(), locale),
                })}
        </p>
        <button
          onClick={() => navigate({ to: afterPlanPath(), replace: true })}
          className="cta-primary mt-3"
        >
          {t('addYourChild')}
        </button>
      </div>
    );
  }

  if (ownershipBlocked) {
    return (
      <div className="flex flex-1 flex-col bg-surface">
        <div className="safe-area-top flex shrink-0 items-center px-3 pt-2">
          <button
            aria-label={t('close')}
            onClick={closePaywall}
            className="flex size-11 cursor-pointer items-center justify-center rounded-pill text-content transition-colors hover:bg-bg"
          >
            <IconX size={20} stroke={2.5} />
          </button>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
          <IconDeviceMobile size={28} stroke={2} className="text-muted" aria-hidden />
          <h1 className="font-display text-xl text-content">
            {ownedStore === undefined
              ? t('working')
              : ownedStore
                ? t('managedElsewhere', { store: t(`storeNames.${ownedStore}`) })
                : t('billingOwnershipUnknown')}
          </h1>
          <p className="text-base text-muted">
            {ownedStore ? t('managedElsewhereBody') : t('billingOwnershipUnknownBody')}
          </p>
          {managementError && (
            <p role="alert" className="text-sm text-error">
              {managementError}
            </p>
          )}
          <button
            onClick={ownedStore ? openStoreBilling : retryBillingOwnership}
            disabled={managementBusy || ownedStore === undefined}
            className="cta-primary mt-4"
          >
            {managementBusy || ownedStore === undefined
              ? t('working')
              : ownedStore
                ? t('manageBilling')
                : t('checkAgain')}
          </button>
        </div>
      </div>
    );
  }

  const priceLabel = native ? (selected?.product.priceString ?? '…') : '—';

  // From tier ranks only (the period is async); a pricing drop is always deferred (decision 71), whatever the seat does.
  const isDowngradeChange = (() => {
    if (!changing || !ownedSeat || !access) return false;
    const r = (list: { id: string }[], id: string) => list.findIndex((x) => x.id === id);
    const seatDelta = r(SEAT_TIERS, seat) - r(SEAT_TIERS, ownedSeat);
    const pricingDelta = r(PRICING_TIERS, pricing) - r(PRICING_TIERS, access.pricing_tier);
    if (r(SEAT_TIERS, ownedSeat) < 0 || r(SEAT_TIERS, seat) < 0) return false;
    if (r(PRICING_TIERS, access.pricing_tier) < 0 || r(PRICING_TIERS, pricing) < 0) return false;
    const down = seatDelta < 0 || pricingDelta < 0;
    const up = seatDelta > 0 || pricingDelta > 0;
    return pricingDelta < 0 || (down && !up);
  })();

  // Default to eligible while access loads, so the trial framing doesn't flash on then off.
  const trialAvailable = !subscribed && access?.trial_eligible !== false;
  const showTrial = selectedTier.trial && trialAvailable;
  const trialBlockedByPriorUse =
    selectedTier.trial && !subscribed && access?.trial_eligible === false;
  const stepLabel =
    step === 1
      ? subscribed
        ? t('stepYourPlan')
        : t('stepChoosePlan')
      : showTrial
        ? t('stepHowTrialWorks')
        : t('stepConfirm');
  const ctaLabel = showTrial
    ? t('startMyFreeTrial')
    : revertingPending
      ? t('keepPlan', { tierName: selectedTier.name })
      : changing
        ? t('switchTo', { tierName: selectedTier.name })
        : t('subscribe');

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface">
      <div className="safe-area-top flex shrink-0 items-center justify-between px-3 pt-2 xl:mx-auto xl:w-full xl:max-w-md">
        {step === 1 && isOnboardingEntry ? (
          <div className="size-11" />
        ) : (
          <button
            aria-label={step === 1 ? t('close') : t('back')}
            onClick={() =>
              step === 1 ? closePaywall() : checkout ? setCheckout(null) : setStep(1)
            }
            className="flex size-11 cursor-pointer items-center justify-center rounded-pill text-content transition-colors hover:bg-bg"
          >
            {step === 1 ? (
              <IconX size={20} stroke={2.5} />
            ) : (
              <IconChevronLeft size={20} stroke={2.5} />
            )}
          </button>
        )}
        <span className="pr-2 text-sm font-bold text-muted">{stepLabel}</span>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-6 pt-2">
        {step === 1 ? (
          <>
            {subscribed ? (
              <div className="xl:mx-auto xl:w-full xl:max-w-md">
                <h1 className="font-display text-2xl text-content">{t('yourPlan')}</h1>
                <p className="mt-1 text-base text-muted">
                  {t('seatsUsed', {
                    used: seatedStudents,
                    total: seatTier(currentSeat!).maxStudents,
                  })}
                </p>
              </div>
            ) : (
              <div className="text-center xl:mx-auto xl:w-full xl:max-w-md">
                <div className="mx-auto flex size-16 items-center justify-center rounded-pill bg-primary/8 text-primary">
                  <IconSparkles size={30} stroke={2} aria-hidden />
                </div>
                <h1 className="mt-2 font-display text-2xl text-content">
                  {trialAvailable ? t('tryFamilySolo') : t('onePlanEveryChild')}
                </h1>
                <p className="mt-1 text-base text-muted">{t('onePlanEveryChildBody')}</p>
              </div>
            )}

            <div className="mt-1 flex flex-col gap-1.5 xl:mx-auto xl:w-full xl:max-w-md">
              <p id="syllabus-question" className="text-sm font-semibold text-content">
                {t('syllabusQuestion')}
              </p>
              <div
                role="group"
                aria-labelledby="syllabus-question"
                className="flex w-full rounded-md bg-bg p-0.5"
              >
                {PRICING_TIERS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={pricing === p.id}
                    onClick={() => {
                      pricingPicked.current = true;
                      setPricing(p.id);
                    }}
                    className={`min-h-11 flex-1 cursor-pointer rounded-md text-sm font-bold transition-all ${
                      pricing === p.id ? 'bg-surface text-content shadow-sm' : 'text-muted'
                    }`}
                  >
                    {t(p.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <p className="-mt-1 text-sm text-muted xl:mx-auto xl:w-full xl:max-w-md">
              {t('pricingIncluded', {
                detail: t(
                  PRICING_TIERS.find((p) => p.id === pricing)?.detailKey ?? 'pricingStandard'
                ),
              })}
            </p>

            <div className="mt-1 flex flex-col gap-2.5 xl:mx-auto xl:w-full xl:max-w-md">
              {SEAT_TIERS.map((t) => (
                <SeatTierCard
                  key={t.id}
                  tier={t}
                  selected={seat === t.id}
                  // Must match pricing too: Standard and +IGCSE share the same seat tiers.
                  current={t.id === currentSeat && pricing === access?.pricing_tier}
                  // Web only: calling off a pending change on Play needs a store replacement the app can't issue.
                  selectableWhenCurrent={Boolean(access?.pending_seat_tier) && !nativePlatform}
                  hideTrialRibbon={subscribed || access?.trial_eligible === false}
                  // undefined hides the price column off-device; null would show a '…' that never resolves.
                  price={native ? monthlyPriceFor(t.id) : undefined}
                  blockedReason={blockedReason(t.maxStudents)}
                  onSelect={() => setSeat(t.id)}
                  onBlockedSelect={() => setPickerTier(t)}
                />
              ))}
            </div>

            {pendingPurchase.pending && (
              <div
                role="status"
                className="rounded-lg border border-border p-4 text-center xl:mx-auto xl:w-full xl:max-w-md"
              >
                <p className="text-base text-muted">{t('storePurchasePending')}</p>
                <button
                  onClick={() => void pendingPurchase.refresh()}
                  disabled={pendingPurchase.checking}
                  className="min-h-11 cursor-pointer px-4 text-sm font-bold text-primary disabled:opacity-40"
                >
                  {pendingPurchase.checking ? t('working') : t('checkAgain')}
                </button>
              </div>
            )}
            {loadError && (
              <p className="text-sm font-semibold text-error xl:mx-auto xl:w-full xl:max-w-md">
                {loadError}
              </p>
            )}

            {access?.pending_seat_tier && (
              <p className="text-center text-sm font-semibold text-primary xl:mx-auto xl:w-full xl:max-w-md">
                {access.pending_effective_at
                  ? t('pendingChangeOn', {
                      tierName:
                        SEAT_TIER_NAMES[access.pending_seat_tier] ?? access.pending_seat_tier,
                      date: fmtDayMonth(access.pending_effective_at, locale),
                    })
                  : t('pendingChangeSoon', {
                      tierName:
                        SEAT_TIER_NAMES[access.pending_seat_tier] ?? access.pending_seat_tier,
                    })}
              </p>
            )}
            {managementError && (
              <p role="alert" className="text-sm text-error xl:mx-auto xl:w-full xl:max-w-md">
                {managementError}
              </p>
            )}
            {error && (
              <p
                role="alert"
                className="text-sm font-semibold text-error xl:mx-auto xl:w-full xl:max-w-md"
              >
                {error}
              </p>
            )}
            {(subscribed || nativePlatform) && (
              <div className="mt-2 divide-y divide-border-light overflow-hidden rounded-lg border border-border xl:mx-auto xl:w-full xl:max-w-md">
                {subscribed && (
                  <button
                    onClick={openStoreBilling}
                    disabled={managementBusy}
                    className="flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-bg disabled:opacity-40"
                  >
                    <IconCreditCard
                      size={18}
                      stroke={2}
                      className="shrink-0 text-muted"
                      aria-hidden
                    />
                    <span className="flex-1 text-sm font-bold text-content">
                      {t('manageBilling')}
                    </span>
                    <IconExternalLink
                      size={15}
                      stroke={2}
                      className="shrink-0 text-hint"
                      aria-hidden
                    />
                  </button>
                )}
                {nativePlatform && (
                  <button
                    onClick={restore}
                    disabled={!native || busy || Boolean(pendingPurchase.pending)}
                    className="flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-bg disabled:opacity-40"
                  >
                    <IconRefresh size={18} stroke={2} className="shrink-0 text-muted" aria-hidden />
                    <span className="flex-1 text-sm font-bold text-content">
                      {t('restorePurchases')}
                    </span>
                  </button>
                )}
              </div>
            )}
          </>
        ) : checkout ? (
          <div className="flex flex-col gap-3 xl:mx-auto xl:w-full xl:max-w-md">
            <p className="text-base text-muted">
              {checkout.kind === 'card'
                ? t('cardNeededToSwitch', { plan: selectedTier.name })
                : `${selectedTier.name} · ${PRICING_TIERS.find((p) => p.id === pricing)?.name}`}
            </p>
            <EmbeddedCheckout
              clientSecret={checkout.clientSecret}
              onComplete={() =>
                void (checkout.kind === 'card'
                  ? finishCardSetup(checkout.sessionId)
                  : finishWebCheckout(checkout.sessionId))
              }
              onError={() => {
                setCheckout(null);
                setError(t('errPurchaseFailed'));
              }}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3 xl:mx-auto xl:w-full xl:max-w-md">
            <div>
              <h1 className="font-display text-2xl text-content">
                {showTrial ? t('trialHeading', { days: TRIAL_DAYS }) : t('confirmYourPlan')}
              </h1>
              <p className="mt-1 text-base text-muted">
                {selectedTier.name} · {PRICING_TIERS.find((p) => p.id === pricing)?.name}
              </p>
            </div>

            {/* A revert ignores `annual`; switching period is a separate trip through this screen. */}
            {!revertingPending && (
              <div
                role="group"
                aria-label={t('billingPeriod')}
                className="mt-1 flex w-full rounded-md bg-bg p-0.5"
              >
                {[
                  { label: t('monthly'), value: false },
                  {
                    label: annualSave > 0 ? t('annualSave', { pct: annualSave }) : t('annual'),
                    value: true,
                  },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    aria-pressed={annual === opt.value}
                    onClick={() => setAnnual(opt.value)}
                    className={`min-h-11 flex-1 cursor-pointer rounded-md text-sm font-bold transition-all ${
                      annual === opt.value ? 'bg-surface text-content shadow-sm' : 'text-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {showTrial ? (
              <>
                {native && (
                  <p className="text-center text-sm text-content">
                    {annual
                      ? t('thenPriceYearly', {
                          days: TRIAL_DAYS,
                          price: selected?.product.priceString ?? '…',
                          date: dateInDays(TRIAL_DAYS, locale),
                        })
                      : t('thenPriceMonthly', {
                          days: TRIAL_DAYS,
                          price: selected?.product.priceString ?? '…',
                          date: dateInDays(TRIAL_DAYS, locale),
                        })}
                  </p>
                )}
                <div className="mt-2">
                  <TimelineStep
                    Icon={IconLockOpen}
                    title={t('timelineTodayTitle')}
                    body={t('timelineTodayBody')}
                  />
                  <TimelineStep
                    Icon={IconBell}
                    title={t('timelineDay', { n: TRIAL_REMINDER_DAY })}
                    body={t('timelineReminderBody')}
                    accent
                  />
                  <TimelineStep
                    Icon={IconCreditCard}
                    title={t('timelineDay', { n: TRIAL_DAYS })}
                    body={t('timelineBilledBody', {
                      date: dateInDays(TRIAL_DAYS, locale),
                      native: String(nativePlatform),
                    })}
                    accent
                    last
                  />
                </div>
              </>
            ) : (
              <div className="mt-1 flex flex-col gap-2 rounded-lg border border-border p-4">
                {/* Hidden on a revert: `annual` isn't changeable there, so this would quote the wrong period's price. */}
                {!revertingPending && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">
                      {selectedTier.name} · {PRICING_TIERS.find((p) => p.id === pricing)?.name}
                    </span>
                    <span className="font-bold text-content">{priceLabel}</span>
                  </div>
                )}
                <div
                  className={`flex justify-between text-sm${
                    revertingPending ? '' : ' border-t border-border-light pt-2'
                  }`}
                >
                  <span className="font-bold text-content">{t('billedToday')}</span>
                  <span className="font-bold text-content">
                    {isDowngradeChange || revertingPending ? t('nothingToday') : priceLabel}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-muted">
                  {revertingPending
                    ? t('keepPlanNote')
                    : endsTrialNow
                      ? t('trialEndsNowNote', {
                          period: annual ? t('renewYearly') : t('renewMonthly'),
                        })
                      : changing
                        ? isDowngradeChange
                          ? t('downgradeNote')
                          : t('proratedNote')
                        : trialBlockedByPriorUse
                          ? t('trialAlreadyUsedNote', {
                              period: annual ? t('renewYearly') : t('renewMonthly'),
                            })
                          : t('renewsNote', {
                              period: annual ? t('renewYearly') : t('renewMonthly'),
                            })}
                </p>
              </div>
            )}

            {error && (
              <p role="alert" className="text-sm font-semibold text-error">
                {error}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 px-6 pb-6 pt-3">
        <div className="flex flex-col gap-2 xl:mx-auto xl:w-full xl:max-w-md">
          {step === 1 ? (
            <>
              <button
                onClick={() => (syllabusDowngradeBlocked ? setShowSyllabusBlock(true) : setStep(2))}
                disabled={Boolean(pendingPurchase.pending) || (subscribed && !changing)}
                className="cta-primary"
              >
                {/* Step 1's button is normally just "Continue" — it advances to
                    the confirm screen. The revert case is the exception: the
                    parent is calling off a scheduled change, and "Continue"
                    gives no clue that is what the button does. */}
                {revertingPending
                  ? t('keepPlan', { tierName: selectedTier.name })
                  : showTrial
                    ? t('startMyFreeTrial')
                    : t('continueLabel')}
              </button>
              <div className="flex min-h-20 flex-col gap-2">
                <p className="text-center text-sm text-muted">
                  {alreadyPending
                    ? t('alreadyScheduled')
                    : subscribed && !changing
                      ? t('pickDifferentPlan')
                      : t('cancelAnytime')}
                </p>
              </div>
            </>
          ) : checkout ? null : (
            <>
              {pendingPurchase.pending ? (
                <div role="status" className="rounded-lg border border-border p-4 text-center">
                  <p className="text-base text-muted">{t('storePurchasePending')}</p>
                  <button
                    onClick={() => void pendingPurchase.refresh()}
                    disabled={pendingPurchase.checking}
                    className="min-h-11 cursor-pointer px-4 text-sm font-bold text-primary disabled:opacity-40"
                  >
                    {pendingPurchase.checking ? t('working') : t('checkAgain')}
                  </button>
                </div>
              ) : pendingSessionId ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-border p-4 text-center">
                  <p className="text-sm leading-relaxed text-muted">{t('checkoutPending')}</p>
                  <button
                    onClick={() => void finishWebCheckout(pendingSessionId)}
                    disabled={busy}
                    className="min-h-11 cursor-pointer px-4 text-sm font-bold text-primary disabled:opacity-40"
                  >
                    {busy ? t('working') : t('checkAgain')}
                  </button>
                </div>
              ) : native ? (
                <>
                  {showTrial && selected && !nativePlatform && (
                    <div className="flex items-center gap-3 rounded-lg border border-primary/10 bg-primary/5 p-3">
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-pill bg-primary/10 text-primary">
                        <IconCreditCard size={17} stroke={2} aria-hidden />
                      </span>
                      <div>
                        <p className="text-sm font-bold text-content">
                          {t('trialNoCardTitle', {
                            price: selected.product.priceString,
                            date: dateInDays(TRIAL_DAYS, locale),
                          })}
                        </p>
                        <p className="mt-0.5 text-sm leading-relaxed text-muted">
                          {t('trialNoCardBody')}
                        </p>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => void buy()}
                    disabled={!selected || busy || Boolean(pendingPurchase.pending)}
                    className="cta-primary"
                  >
                    {busy ? t('working') : ctaLabel}
                  </button>
                </>
              ) : (
                // Purchases go through the store, so the web build can't finish
                // this. Say so as its own block — the old build degraded to the
                // words "app only" where a price goes, which read as a price.
                <div className="flex items-start gap-3 rounded-lg border border-border p-4">
                  <IconDeviceMobile size={18} stroke={2} className="mt-0.5 shrink-0 text-muted" />
                  <div>
                    <p className="text-sm font-bold text-content">{t('finishInApp')}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted">
                      {t('finishInAppBody')}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex min-h-20 flex-col gap-2">
                {/* A cardless web trial doesn't auto-renew. */}
                {native && !pendingSessionId && !(showTrial && !nativePlatform) && (
                  <p className="text-center text-sm text-muted">{t('cancelAutoRenew')}</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {pickerTier && (
        <SeatPickerSheet
          open
          onClose={() => setPickerTier(null)}
          tierName={pickerTier.name}
          maxStudents={pickerTier.maxStudents}
          members={household?.members ?? []}
          parentLinks={parentLinks}
          onReleased={onReleased}
        />
      )}

      <DowngradeSyllabusBlockedSheet
        open={showSyllabusBlock}
        onClose={() => setShowSyllabusBlock(false)}
        onManageChildren={() => navigate({ to: '/children' })}
        childNames={igcseChildren.map((m) => m.name)}
      />
    </div>
  );
}

function TimelineStep({
  Icon,
  title,
  body,
  last,
  accent,
}: {
  Icon: typeof IconBell;
  title: string;
  body: string;
  last?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className={`flex size-8 shrink-0 items-center justify-center rounded-pill text-surface ${accent ? 'bg-accent' : 'bg-primary'}`}
        >
          <Icon size={16} stroke={2} aria-hidden />
        </span>
        {!last && <span className="w-[3px] flex-1 bg-primary/15" />}
      </div>
      <div className={last ? '' : 'pb-4'}>
        <p className="text-sm font-bold text-content">{title}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-muted">{body}</p>
      </div>
    </div>
  );
}
